// Pure state machine for the sync indicator UI.
//
// Tracer 11 (#654). The Stimulus controller (`sync_indicator_controller.js`)
// listens to events on the SyncEngine and delegates colour transitions to
// `nextIndicatorState` so the logic can be unit-tested without DOM mounting.
//
// Colour semantics (PRD §sync indicator):
//   green   — everything synced, nothing pending.
//   yellow  — actively syncing (in flight).
//   orange  — pending changes waiting for the next sync tick.
//   red     — error or conflict; needs user attention.

export type IndicatorState = "green" | "yellow" | "orange" | "red";

export const INDICATOR_STATES = {
  GREEN:  "green",
  YELLOW: "yellow",
  ORANGE: "orange",
  RED:    "red",
} as const satisfies Record<string, IndicatorState>;

export type IndicatorEvent =
  | "sync:syncing"
  | "sync:success"
  | "sync:pending"
  | "sync:attention"
  | "sync:error"
  | "sync:conflict"
  | (string & {});

export function nextIndicatorState(
  current: IndicatorState,
  event: IndicatorEvent,
): IndicatorState {
  switch (event) {
    case "sync:syncing":
      return INDICATOR_STATES.YELLOW;
    case "sync:success":
      return INDICATOR_STATES.GREEN;
    case "sync:pending":
    // #883 — dead-lettered journal ops (permanent 404/422 rejections) need
    // manual review in the recovery panel. Amber, not red: the data is safe
    // locally and nothing is actively failing, but "all green" would lie.
    case "sync:attention":
      return INDICATOR_STATES.ORANGE;
    case "sync:error":
    case "sync:conflict":
      return INDICATOR_STATES.RED;
    default:
      return current;
  }
}
